import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiSun, FiArrowUp, FiArrowLeft, FiZap } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import ModuleHeader from "../../moduleHeader";
import {
  getPlanetsData,
  getHousesData,
  HouseModal,
  HouseList,
  PlanetList,
  BirthChartComponent,
  FutureInterpretationComponent,
  TabButton,
  MainTabButton,
  TabContainer,
  MainTabContainer,
} from "./index.js";

const Planets = ({
  initialTab = "gezegenler",
  embedded = false,
  onClose,
  birthChartData,
  frontendChart,
  user,
  packageData,
}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState(initialTab || "gezegenler");
  const [bgImageUrl, setBgImageUrl] = useState('');
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);

  useEffect(() => {
    setActiveTab(initialTab || "gezegenler");
  }, [initialTab]);

  // Arka plan görselini dinamik olarak yükle
  useEffect(() => {
    if (!embedded) {
      const bgUrl = `${process.env.PUBLIC_URL || ''}/assets/birthChart/birth-chart-bg.png`;
      setBgImageUrl(bgUrl);
    }
  }, [embedded]);

  const planets = getPlanetsData(birthChartData);
  const houses = getHousesData(birthChartData);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const [isHouseModalOpen, setIsHouseModalOpen] = useState(false);
  const [selectedHouse, setSelectedHouse] = useState(null);

  const openHouseModal = (house) => {
    setSelectedHouse(house);
    setIsHouseModalOpen(true);
  };

  const closeHouseModal = () => {
    setIsHouseModalOpen(false);
    setSelectedHouse(null);
  };

  return (
    <div
      className={
        embedded
          ? "w-full h-full flex items-stretch justify-stretch bg-transparent"
          : "w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed]"
      }
      style={{
        backgroundImage: bgImageUrl ? `url(${bgImageUrl})` : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div
        className={
          embedded
            ? "flex flex-col items-center w-full h-full bg-transparent shadow-none border-0 rounded-none relative overflow-y-auto"
            : "flex flex-col items-center w-full min-h-full shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto"
        }
      >
        {/* Standard Module Header */}
        {packageData && (
          <div className="relative z-30 w-full mb-2">
            <ModuleHeader
              userName={packageData.user?.first_name + " " + (packageData.user?.last_name || "")}
              subtitle="Kişisel Doğum Haritası Analizi"
              title="Doğum Haritası"
              description={packageData.package?.description}
              backgroundImage={packageData.package?.image_url}
              onExpandedChange={setIsHeaderExpanded}
              showToggle={true}
              autoHeight={true}
            />
          </div>
        )}

        <div className={`w-full flex-1 bg-white relative z-20 transition-all duration-300 ${isHeaderExpanded ? "mt-0" : "-mt-6 rounded-t-[40px] shadow-[0_-20px_40px_rgba(0,0,0,0.05)]"}`}>
          {/* Quick Info Bar - Sun & Rising Signs */}
          <div className="w-full px-8 pt-8 flex items-center gap-3">
            <div className="flex-1 flex items-center gap-2 bg-gradient-to-r from-orange-50 to-orange-100/50 p-2.5 rounded-2xl border border-orange-200/50 shadow-sm">
              <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm">
                <FiSun className="w-4 h-4 text-orange-400" />
              </div>
              <div className="flex flex-col items-start overflow-hidden">
                <span className="text-[10px] font-black text-orange-400/80 uppercase tracking-widest leading-none mb-1 whitespace-nowrap text-left">Güneş Burcu</span>
                <span className="text-sm font-black text-gray-800 leading-none whitespace-nowrap text-left">
                  {(() => {
                    const planetsArr = birthChartData?.planets ? Object.values(birthChartData.planets) : [];
                    const sun = planetsArr.find((p) => p?.planet_name === "Sun");
                    return sun?.zodiac_sign || "-";
                  })()}
                </span>
              </div>
            </div>

            <div className="flex-1 flex items-center gap-2 bg-gradient-to-r from-cyan-50 to-cyan-100/50 p-2.5 rounded-2xl border border-cyan-200/50 shadow-sm">
              <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm">
                <FiArrowUp className="w-4 h-4 text-cyan-400" />
              </div>
              <div className="flex flex-col items-start overflow-hidden">
                <span className="text-[10px] font-black text-cyan-400/80 uppercase tracking-widest leading-none mb-1 whitespace-nowrap text-left">Yükselen Burç</span>
                <span className="text-sm font-black text-gray-800 leading-none whitespace-nowrap text-left">
                  {(() => {
                    const housesArr = birthChartData?.houses ? Object.values(birthChartData.houses) : [];
                    const h1 = housesArr.find((h) => h?.house_number === 1);
                    return h1?.zodiac_sign || "-";
                  })()}
                </span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="w-full flex-col">
            <div className="bg-transparent rounded-lg p-4 py-1">
              {/* Modern Tab Switcher */}
              <div className="w-full mb-6">
                <div className="bg-white/40 backdrop-blur-md p-1.5 rounded-3xl flex items-center relative gap-1 border border-white/20 shadow-sm">
                  {[
                    { id: "gezegenler", label: "Gezegenler" },
                    { id: "evler", label: "Evler" },
                    { id: "dogum", label: "Doğum Haritası" }
                  ].map((tab) => {
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => handleTabChange(tab.id)}
                        className={`relative flex-1 py-2 rounded-2xl text-[12px] font-black transition-all duration-500 whitespace-nowrap ${isActive ? "text-white" : "text-gray-500 hover:text-gray-700"
                          }`}
                      >
                        {isActive && (
                          <motion.div
                            layoutId="birthTab"
                            className="absolute inset-0 bg-[#3DA7B5] rounded-2xl shadow-lg shadow-[#3DA7B5]/20"
                            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                          />
                        )}
                        <span className="relative z-10">{tab.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.3 }}
                  >
                    {activeTab === "gezegenler" && <PlanetList planets={planets} />}

                    {activeTab === "evler" && (
                      <HouseList houses={houses} onHouseClick={openHouseModal} />
                    )}

                    {activeTab === "dogum" && (
                      <BirthChartComponent frontendChart={frontendChart} birthChartData={birthChartData} />
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Transit Analysis CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-12 mb-8 px-2"
              >
                <button
                  onClick={() => navigate(`/${id}/27`)}
                  className="w-full relative overflow-hidden group bg-gradient-to-r from-[#3DA7B5] to-[#5ABCC8] p-6 rounded-3xl shadow-xl shadow-[#3DA7B5]/20 active:scale-[0.98] transition-all"
                >
                  <div className="absolute top-0 right-0 p-8 bg-white/10 rounded-bl-[3rem] -mr-8 -mt-8 blur-2xl group-hover:blur-xl transition-all" />
                  <div className="relative z-10 flex items-center justify-between">
                    <div className="flex flex-col items-start gap-1">
                      <span className="text-[10px] font-black text-white/70 uppercase tracking-widest">Geleceğini Keşfet</span>
                      <h4 className="text-[15px] font-black text-white leading-tight whitespace-nowrap">Transit Analizini Hemen Yap!</h4>
                    </div>
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center text-white group-hover:translate-x-1 transition-transform">
                      <FiZap className="fill-white" size={24} />
                    </div>
                  </div>
                </button>
              </motion.div>
            </div>
            <div className="w-full h-8 mb-16"> </div>
          </div>

          {/* House Detail Modal */}
          <HouseModal
            isOpen={isHouseModalOpen}
            onClose={closeHouseModal}
            selectedHouse={selectedHouse}
          />
        </div>
      </div>
    </div>
  );
};

export default Planets;
